'use strict';

define('vb/private/events/baseEventModel',[
  'vb/private/constants',
  'vb/private/log',
], (Constants, Log) => {
  /**
   * @typedef {import('vb/private/stateManagement/container').default} Container
   * @typedef {import('vb/private/events/eventRegistry').default} EventRegistry
   */

  const logger = Log.getLogger('/vb/private/events/eventModel');

  // an ordered list of container classes, used for checking if an Event reference is allowed
  // TODO: follow up on layout and fragments, local events are accessible regardless of this
  // ordered list, but we will need to ensure whether layout and fragments need access to anything else
  const CLASSES = [
    'application',
    'flow',
    'page',
    'apppackage',
    'packageflow',
    'packagepage',
    'applicationextension',
    'apppackageextension',
    'flowextension',
    'pageextension',
  ];

  /**
   * A model for event definitions, both declared and undeclared.
   *
   * Along with name, defining container (if one), and other convenience properties,
   * this contains a "behavior" {EventBehavior} property.
   *
   * Any model returned from the registry extends this model.
   */
  class BaseEventModel {
    /**
     * Constructs a new instance of the BaseEventModel class.
     *
     * @param {String} name - The name of the event.
     * @param {EventRegistry} registry - The event registry, required to prevent circular dependencies.
     * @param {Object} [definition] - The JSON declaration of the event, containing properties below.
     * @param {String} [definition.behavior] - The behavior type of the event (e.g., 'notify', 'notifyAndWait',
     * 'checkForCancel', 'transform' (deprecated), 'transformPayload', 'dynamicComponent').
     * @param {String} [definition.propagationBehavior] - The propagation behavior type for fragment and layout events
     * only. Possible values are 'self' (default) or 'container'
     * @param {String} [definition.mode] - The mode of the event (e.g., 'listenable', 'triggerable').
     * @param {String} [definition.autoWire] - The auto-wired event listener name.
     * @param {Object} [definition.payloadType] - The expected type of the event payload.
     * @param {Object} [definition.returnType] - The expected return type of the event.
     * @param {Container|null} [container] - The declaring container, or null if not declared.
     * @param {boolean} [isInterface=false] - Whether the event is declared in the container's interface section.
     */
     constructor(name, registry, definition, container, isInterface = false) {
      this.name = name;

      if (definition) {
        this.payloadType = definition.payloadType;
        this.autoWire = definition.autoWire;
        this.returnType = definition.returnType;
        this.mode = definition.mode;
        this.behavior = definition.behavior;
        this.propagationBehavior = definition.propagationBehavior;
      }

      this.container = container;

      this.isDeclared = !!container; // only true if we were given a declaring container

      // the fully-qualified name, using extension namespace/prefix:
      //   <extension namespace>/<container prefix>:<simple name>
      // - extension namespace: extension ID or 'base'
      // - container prefix: 'global', 'application', 'flow', 'page', 'layout' or 'fragment'
      // - simple name: the event name, with no prefix/namespace

      if (container) {
        this.fullName = `${container.extensionId || Constants.ExtensionNamespaces.BASE}/`
          + `${BaseEventModel.basePrefix(container)}:${name}`;
      } else {
        this.fullName = name;
      }

      this.isInterface = isInterface;
    }

    /**
     * checks if the event is accessible to the container.
     * In any given context, we only have access to one of each Page / Flow / Application,
     * even though Flows can have sub-Flows (with Pages, etc.).
     *
     * The registrar's scopeResolver must have the container that is asking for the registration.
     *
     * @param container
     * @param name the name used by the reference.
     * @returns {boolean} false if the name is not properly qualified or the event in not defined container scope.
     */
    accessibleBy(container, name) {
      if (this.container.className === 'Application') {
        // everyone can access;
        return true;
      }

      const autoWiredContainer = this.autoWiredContainer();
      if (autoWiredContainer
        && (container === autoWiredContainer || (container.isExtension() && container.base === autoWiredContainer))) {
        return true;
      }

      const nameParts = BaseEventModel.parseName(container, name);

      if (container.isExtension()) {
        // if properly qualified names are used, check that the name's 'ext' resolves the same
        // for the container (that we are checking for) as it resolves for the registrar
        const partsForRegistrar = BaseEventModel.parseName(this.container, this.name);
        if (nameParts.ext !== partsForRegistrar.ext) {
          return false;
        }

        // check if it's defined locally in the extension:
        const scope = container.scopeResolver[nameParts.prefix];
        const isLocalEvent = scope && !!scope.hasEvent(nameParts.name);
        if (isLocalEvent) {
          return true;
        }
      }

      // when getting the scopeResolver and className to check access, we want it for the 'base' container,
      // not the extension. If the container is not an extension, the 'base' is the container itself.
      // For example, for a PageExtension, we want the Page's scopeResolver, and we look for the 'page'.
      const base = BaseEventModel.baseContainer(container);
      const resolver = base.scopeResolver;

      // the event is accessible if the scope resolver for the container that fired the event
      // has a property that is the container that is requesting the event registration,
      // AND if the requesting container is an equal or lower class of the registrar.
      // For example, a Flow's scopeResolver will have only 'application' and 'flow' (and 'this').
      // If the current container's  className.toLowerCase() is 'page', this returns false.
      // If it is 'flow', make sure the 'flow' in the resolver is the same Flow as the one asking for the event.
      return !!(resolver && resolver[this.container.eventPrefix] === this.container)
        && (CLASSES.indexOf(container.className.toLowerCase()) >= CLASSES.indexOf(this.container.eventPrefix));
    }

    /**
     * Returns true if the event is enabled for auto-wiring (ie, has the autoWire property
     * set to an event listener name).
     * @returns {boolean} true if the auto-wiring is enabled; false otherwise
     */
    isAutoWireEnabled() {
      return !!this.autoWire;
    }

    /**
     * Returns the auto-wired container or null if the event is not auto-wired.
     * @returns {Container|null}
     */
    autoWiredContainer() {
      if (this.isAutoWireEnabled() && this.container.allowsEventAutoWiring(this)) {
        const parentContainer = this.container.parent;
        return parentContainer && parentContainer.isExtension() ? parentContainer.base : parentContainer;
      }

      return null;
    }

    /**
     * Returns final container for the event propagation purposes.
     * @returns {Container}
     */
    terminalContainer() {
      return this.autoWiredContainer() || this.container;
    }

    /**
     * return the base container (page, flow, application).
     * @param container
     * @returns {*}
     */
    static baseContainer(container) {
      return container.base || container;
    }

    /**
     * return the base container class, as lower case, for use in event names/keys
     * @param container
     * @returns {string}
     */
    static basePrefix(container) {
      return BaseEventModel.baseContainer(container).eventPrefix;
    }

    /**
     * <'base'|''|extensionId>/<container|''>:<name>
     *
     * @param container
     * @param name
     * @returns {*|string[]|void}
     */
    static parseName(container, name) {
      const parts = name.split(':');

      let namespace;
      let simpleName;
      if (parts.length === 2) {
        [namespace, simpleName] = parts;
      } else if (parts.length === 1) {
        [simpleName] = parts;
        const containerExtensionContext = container.extensionId || Constants.ExtensionNamespaces.BASE;
        const prefix = BaseEventModel.basePrefix(container);
        namespace = `${containerExtensionContext}/${prefix}`;
      } else {
        logger.error('Invalid event name', name);
        return null;
      }

      const namespaceParts = namespace.split('/');

      if (namespaceParts.length === 2) {
        // assume 'base' when it starts with a slash
        if (!namespaceParts[0]) {
          namespaceParts[0] = Constants.ExtensionNamespaces.BASE;
        }
      } else if (namespaceParts.length === 1) {
        // no slash, if it has an extensionId, use it; otherwise, use 'base'
        // if prefix is 'global', always use 'base'
        const extPrefix = namespaceParts[0] === Constants.GLOBAL_PREFIX ? Constants.ExtensionNamespaces.BASE
          : container.extensionId || Constants.ExtensionNamespaces.BASE;
        namespaceParts.unshift(extPrefix);
      }

      if (!namespaceParts[1]) {
        namespaceParts[1] = BaseEventModel.basePrefix(container);
      }

      return {
        ext: namespaceParts[0],
        prefix: namespaceParts[1], // container: 'application', 'page', or 'flow'
        name: simpleName,
      };
    }
  }

  return BaseEventModel;
});

